import React, { useState } from 'react';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { UserPlus, MessageCircle, UserMinus, Ban, Check, X, Search } from 'lucide-react';
import { toast } from 'sonner';

export const FriendsView = () => {
  const { friends, friendRequests, members, sendFriendRequest, acceptFriend, declineFriend, removeFriend, blockUser, createDM, selectDm, fetchDMs } = useApp();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [tab, setTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [addUsername, setAddUsername] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  const handleAddFriend = async () => {
    if (!addUsername.trim()) return;
    setAddLoading(true);
    try {
      const target = members.find(m => m.username === addUsername.trim().toLowerCase() && m.user_id !== user.user_id);
      if (!target) { toast.error('User not found'); return; }
      await sendFriendRequest(target.user_id);
      toast.success('Friend request sent!');
      setAddUsername('');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAddLoading(false);
    }
  };

  const handleSendDM = async (friendId) => {
    const dm = await createDM(friendId);
    if (dm) {
      await fetchDMs();
      selectDm({ ...dm, user: friends.find(f => f.user_id === friendId) });
    }
  };

  const filteredFriends = searchQuery
    ? friends.filter(f => f.username?.includes(searchQuery.toLowerCase()) || f.display_name?.toLowerCase().includes(searchQuery.toLowerCase()))
    : friends;

  const onlineFriends = filteredFriends.filter(f => f.status !== 'offline');

  return (
    <div className="flex-1 flex flex-col min-h-0 min-w-0 bg-[#0b0d10]" data-testid="friends-view">
      {/* Header */}
      <div className="h-12 px-4 flex items-center gap-3 border-b border-white/5 flex-shrink-0">
        <span className="text-sm font-semibold text-white">{t('app.friends')}</span>
      </div>

      <div className="flex-1 flex flex-col min-h-0 p-4">
        <Tabs value={tab} onValueChange={setTab} className="flex flex-col flex-1 min-h-0">
          <TabsList className="bg-transparent border-b border-white/5 rounded-none h-auto p-0 gap-0 w-full justify-start mb-4">
            <TabsTrigger value="all" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-cyan-500 data-[state=active]:text-white rounded-none text-zinc-400 text-sm px-4 pb-2" data-testid="friends-tab-all">
              {t('app.all')}
            </TabsTrigger>
            <TabsTrigger value="online" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-cyan-500 data-[state=active]:text-white rounded-none text-zinc-400 text-sm px-4 pb-2" data-testid="friends-tab-online">
              {t('app.online')}
            </TabsTrigger>
            <TabsTrigger value="pending" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-cyan-500 data-[state=active]:text-white rounded-none text-zinc-400 text-sm px-4 pb-2 relative" data-testid="friends-tab-pending">
              {t('app.pending')}
              {friendRequests.incoming.length > 0 && (
                <span className="ml-1 h-4 min-w-4 px-1 bg-red-500 text-white text-[10px] rounded-full inline-flex items-center justify-center">
                  {friendRequests.incoming.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="add" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-cyan-500 data-[state=active]:text-white rounded-none text-zinc-400 text-sm px-4 pb-2" data-testid="friends-tab-add">
              {t('app.addFriend')}
            </TabsTrigger>
          </TabsList>

          {/* Search bar for all/online */}
          {(tab === 'all' || tab === 'online') && (
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
              <Input
                placeholder={t('app.searchMembers')}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-9 bg-[#12141a] border-white/5 text-sm h-9"
                data-testid="friends-search"
              />
            </div>
          )}

          <TabsContent value="all" className="flex-1 min-h-0 mt-0">
            <ScrollArea className="h-full">
              <p className="text-xs text-zinc-500 uppercase font-semibold mb-2">{t('app.all')} — {filteredFriends.length}</p>
              {filteredFriends.map(f => (
                <FriendItem key={f.user_id} friend={f} onDM={() => handleSendDM(f.user_id)} onRemove={() => removeFriend(f.user_id)} onBlock={() => blockUser(f.user_id)} />
              ))}
              {filteredFriends.length === 0 && <p className="text-zinc-600 text-sm text-center py-8">No friends yet</p>}
            </ScrollArea>
          </TabsContent>

          <TabsContent value="online" className="flex-1 min-h-0 mt-0">
            <ScrollArea className="h-full">
              <p className="text-xs text-zinc-500 uppercase font-semibold mb-2">{t('app.online')} — {onlineFriends.length}</p>
              {onlineFriends.map(f => (
                <FriendItem key={f.user_id} friend={f} onDM={() => handleSendDM(f.user_id)} onRemove={() => removeFriend(f.user_id)} onBlock={() => blockUser(f.user_id)} />
              ))}
              {onlineFriends.length === 0 && <p className="text-zinc-600 text-sm text-center py-8">No friends online</p>}
            </ScrollArea>
          </TabsContent>

          <TabsContent value="pending" className="flex-1 min-h-0 mt-0">
            <ScrollArea className="h-full">
              {friendRequests.incoming.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-zinc-500 uppercase font-semibold mb-2">Incoming — {friendRequests.incoming.length}</p>
                  {friendRequests.incoming.map(fr => (
                    <div key={fr.user_id} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5" data-testid={`friend-req-${fr.user_id}`}>
                      <Avatar className="h-9 w-9">
                        <AvatarImage src={fr.avatar} />
                        <AvatarFallback className="bg-cyan-600/20 text-cyan-300 text-xs">{(fr.display_name || fr.username || '?')[0]?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white">{fr.display_name || fr.username}</p>
                        <p className="text-xs text-zinc-500">{fr.username}</p>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => acceptFriend(fr.user_id)} className="h-8 w-8 p-0 text-green-400 hover:bg-green-500/10" data-testid={`accept-${fr.user_id}`}>
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => declineFriend(fr.user_id)} className="h-8 w-8 p-0 text-red-400 hover:bg-red-500/10" data-testid={`decline-${fr.user_id}`}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              {friendRequests.outgoing.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-500 uppercase font-semibold mb-2">Outgoing — {friendRequests.outgoing.length}</p>
                  {friendRequests.outgoing.map(fr => (
                    <div key={fr.user_id} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5">
                      <Avatar className="h-9 w-9">
                        <AvatarImage src={fr.avatar} />
                        <AvatarFallback className="bg-cyan-600/20 text-cyan-300 text-xs">{(fr.display_name || fr.username || '?')[0]?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white">{fr.display_name || fr.username}</p>
                      </div>
                      <span className="text-xs text-zinc-500">{t('app.pending')}</span>
                    </div>
                  ))}
                </div>
              )}
              {friendRequests.incoming.length === 0 && friendRequests.outgoing.length === 0 && (
                <p className="text-zinc-600 text-sm text-center py-8">No pending requests</p>
              )}
            </ScrollArea>
          </TabsContent>

          <TabsContent value="add" className="mt-0">
            <div className="max-w-md">
              <p className="text-sm text-zinc-400 mb-3">Enter a username to send a friend request.</p>
              <div className="flex gap-2">
                <Input
                  placeholder="username"
                  value={addUsername}
                  onChange={e => setAddUsername(e.target.value)}
                  className="bg-[#12141a] border-white/5 text-sm"
                  data-testid="add-friend-input"
                  onKeyDown={e => e.key === 'Enter' && handleAddFriend()}
                />
                <Button onClick={handleAddFriend} disabled={addLoading} className="bg-cyan-600 hover:bg-cyan-700 text-sm" data-testid="add-friend-btn">
                  <UserPlus className="h-4 w-4 mr-1" />
                  {t('app.addFriend')}
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const statusColors = {
  online: 'bg-green-500',
  idle: 'bg-yellow-500',
  dnd: 'bg-red-500',
  offline: 'bg-zinc-600',
};

const FriendItem = ({ friend, onDM, onRemove, onBlock }) => {
  const display = friend.display_name || friend.username;

  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5 group" data-testid={`friend-${friend.user_id}`}>
      <div className="relative flex-shrink-0">
        <Avatar className="h-9 w-9">
          <AvatarImage src={friend.avatar} />
          <AvatarFallback className="bg-cyan-600/20 text-cyan-300 text-xs">{display[0]?.toUpperCase()}</AvatarFallback>
        </Avatar>
        <div className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-[#0b0d10] ${statusColors[friend.status] || statusColors.offline}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white">{display}</p>
        <p className="text-xs text-zinc-500 capitalize">{friend.status || 'offline'}</p>
      </div>
      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button size="sm" variant="ghost" onClick={onDM} className="h-8 w-8 p-0 text-zinc-400 hover:text-white hover:bg-white/10" data-testid={`dm-friend-${friend.user_id}`}>
          <MessageCircle className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="ghost" onClick={onRemove} className="h-8 w-8 p-0 text-zinc-400 hover:text-red-400 hover:bg-red-500/10" data-testid={`remove-friend-${friend.user_id}`}>
          <UserMinus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
